import React, { ReactNode, useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';

// Global Components
import { hasPermission, RestrictedAccess } from '~/components/RestrictedAccess/RestrictedAccess';
import { Add } from '~/components/Add/Add';

// Global Util
import { title } from '~/util/site';

// Local Util

// GraphQL Types
import { ContentSourcesList } from '~/graphqlTypes/ContentSourcesList';
import { AppContextQuery } from '~/graphqlTypes/AppContextQuery';
import { AppContext } from '~/AppContext';

// Queries
import getContentSourcesQuery from './_ContentSourcesList/_ContentSourcesList.gql';

// Subcomponents
import { _ContentSourcesList } from './_ContentSourcesList/_ContentSourcesList';
import { _ContentSourcesCreateSource } from './_ContentSourcesCreateSource';

// Styles
import './ContentSources.scss';

export interface ContentSourcesProps {
  children: ReactNode;
  className?: string;
}

export const ContentSources = ( props: ContentSourcesProps ) => {
  const [createSourceModalIsVisible, setCreateSourceModalIsVisible] = useState<boolean>( false );
  const { loading, error, data } = useQuery<ContentSourcesList>( getContentSourcesQuery );
  const hideCreateSourceModal = () => setCreateSourceModalIsVisible( false );
  const showCreateSourceModal = () => setCreateSourceModalIsVisible( true );
  const context = useContext<AppContextQuery>( AppContext );
  const isAllowed = hasPermission( ContentSources.requiredPermissions, context );

  const { className } = props;

  return (
    // Change div to the most semantically accurate element for your component
    <article
      data-testid="ops-content-sources"
      className={ `ops-content-sources${className ? ` ${className}` : ''}` }
      data-is-restricted={ !isAllowed || undefined }
    >
      <Helmet>
        <title>{ title( 'Audio File Sources' ) }</title>
      </Helmet>
      <h2>
        Audio File Sources{ ' ' }
        <RestrictedAccess permissions={ [] }>
          <Add size="sm" onClick={ showCreateSourceModal } />
        </RestrictedAccess>
      </h2>
      { loading && <p>Loading…</p> }
      { error && <p>Error: { JSON.stringify( error ) }</p> }
      { data && <_ContentSourcesList sources={ data.contentSources } /> }
      <RestrictedAccess permissions={ [] }>
        <_ContentSourcesCreateSource
          className="ops-content-sources__create-source"
          show={ createSourceModalIsVisible }
          onHide={ hideCreateSourceModal }
        />
      </RestrictedAccess>
    </article>
  );
};

ContentSources.displayName = 'ContentSources';

/*
  Specify permissions required to access the component.
  Must be paired with <RestrictedAccess /> to work properly.
*/
ContentSources.requiredPermissions = ['analytics:read'] as string[];
